import {
  applyStep,
  type BackgammonEndpoint,
  type BackgammonPosition,
  type BackgammonTurn,
} from './backgammon.js';
import type { Turn } from '@chessmate/protocol';

/**
 * Assistance hints for the backgammon board: which checkers can move, and which
 * destinations leave a checker exposed to being hit.
 *
 * Both read the roll's `candidates` (every complete legal turn, narrowed by the
 * steps already tapped) — the same list the store's `tap` consults — so a hint
 * can never disagree with what is actually playable, dice obligations included.
 */

/** How exposed a blot is. `direct`: an enemy checker is 1–6 pips behind it (or
 *  can enter onto it), so a single die hits. `indirect`: 7–12 pips away, needs
 *  both dice and an open stepping point. */
export type BlotRisk = 'direct' | 'indirect';

/** Sources (a point number or `'bar'`) that start some candidate's next step. */
export function movableSources(
  candidates: readonly BackgammonTurn[],
  playedCount: number,
): BackgammonEndpoint[] {
  const out = new Set<BackgammonEndpoint>();
  for (const t of candidates) {
    const step = t.steps[playedCount];
    if (step) out.add(step.from);
  }
  return [...out];
}

const RANK: Record<BlotRisk, number> = { indirect: 1, direct: 2 };

/** Own checkers on point `p` in the position's points array (0 when the other colour holds it). */
function ownCount(pos: BackgammonPosition, color: Turn, p: number): number {
  const c = pos.points[p - 1] ?? 0;
  return color === 'w' ? Math.max(c, 0) : Math.max(-c, 0);
}

/**
 * Can the enemy hit a lone `color` checker on `p` next turn?
 *
 * Enemy checkers on the bar must enter first, so while any are waiting the only
 * shot is the die that enters exactly onto `p` (a blot in the entry zone); every
 * other blot is safe for the moment. Otherwise the enemy hits from `distance`
 * pips behind: direct for 1–6, indirect for 7–12 when some way of splitting the
 * distance over two dice lands on a point `color` does not hold with two or more.
 * Doubles that reach further (8, 9, 10, 12) are not modelled.
 */
export function blotRiskAt(pos: BackgammonPosition, color: Turn, p: number): BlotRisk | null {
  const enemy: Turn = color === 'w' ? 'b' : 'w';
  if (pos.bar[enemy] > 0) {
    // Enemy entry: black enters on point = die (1-6), white on 25 - die (19-24).
    const inZone = enemy === 'b' ? p >= 1 && p <= 6 : p >= 19 && p <= 24;
    return inZone ? 'direct' : null;
  }
  // White moves down, black up: an enemy hits `p` from `distance` pips behind.
  const behind = (q: number): number => (enemy === 'b' ? p - q : q - p);
  const enemyAt = (q: number): boolean => {
    const c = pos.points[q - 1] ?? 0;
    return enemy === 'w' ? c > 0 : c < 0;
  };
  let best: BlotRisk | null = null;
  for (let q = 1; q <= 24; q++) {
    if (!enemyAt(q)) continue;
    const d = behind(q);
    if (d >= 1 && d <= 6) return 'direct';
    if (d >= 7 && d <= 12) {
      for (let a = Math.max(1, d - 6); a <= Math.min(6, d - 1); a++) {
        const mid = enemy === 'b' ? q + a : q - a;
        if (mid >= 1 && mid <= 24 && ownCount(pos, color, mid) < 2) {
          best = 'indirect';
          break;
        }
      }
    }
  }
  return best;
}

/** The position after every step of `turn`, for the mover `color`. */
function endOfTurn(pos: BackgammonPosition, color: Turn, turn: BackgammonTurn): BackgammonPosition {
  let cur = pos;
  for (const step of turn.steps) cur = applyStep(cur, color, step);
  return cur;
}

/**
 * For the selected source, the exposure each destination would leave behind.
 * A destination is only listed when EVERY way of finishing the turn from it
 * leaves a blot exposed — if a later die can cover or move the checker, there is
 * nothing to warn about yet. The blots considered are those on any point the
 * finished turn touches (a checker that moves on again is judged where it ends).
 */
export function stepRisks(
  pos: BackgammonPosition,
  candidates: readonly BackgammonTurn[],
  playedCount: number,
  from: BackgammonEndpoint,
): Map<BackgammonEndpoint, BlotRisk> {
  const color = pos.turn;
  const byDest = new Map<BackgammonEndpoint, Array<BlotRisk | null>>();
  for (const turn of candidates) {
    const step = turn.steps[playedCount];
    if (!step || step.from !== from) continue;
    const end = endOfTurn(pos, color, turn);
    let worst: BlotRisk | null = null;
    // Every point this turn touches, not just the first step's: a checker that
    // moves on again (8->7->4) must be judged where it finally stands.
    const touched = new Set<number>();
    for (const st of turn.steps) {
      if (typeof st.from === 'number') touched.add(st.from);
      if (typeof st.to === 'number') touched.add(st.to);
    }
    for (const p of touched) {
      if (ownCount(end, color, p) !== 1) continue;
      const r = blotRiskAt(end, color, p);
      if (r && (worst === null || RANK[r] > RANK[worst])) worst = r;
    }
    const list = byDest.get(step.to) ?? [];
    list.push(worst);
    byDest.set(step.to, list);
  }
  const out = new Map<BackgammonEndpoint, BlotRisk>();
  for (const [dest, risks] of byDest) {
    if (risks.some((r) => r === null)) continue; // some completion is safe
    const least = risks.reduce<BlotRisk>(
      (m, r) => (r !== null && RANK[r] < RANK[m] ? r : m),
      'direct',
    );
    out.set(dest, least);
  }
  return out;
}
